import { Ellipsis, Loader2, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/core/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/core/components/ui/dropdown-menu";
import { Stack } from "@/core/components/ui/stack";
import { Typography } from "@/core/components/ui/typography";

interface RosterMoreActionsMenuProps {
  onImportValues: () => void;
  // Empty until the published script declares a roster contract, and there is
  // nothing to import values into until it does.
  canImportValues: boolean;
  // Only users authorized to update the complete roster receive this callback.
  // Other users do not see an action that the API would reject.
  onSyncTechnicians?: () => void;
  isSyncing?: boolean;
  busy?: boolean;
}

// The occasional roster actions. Adding a person is the thing an owner comes
// here to do and keeps its own button; importing and syncing are periodic, so
// they sit behind the tertiary menu rather than competing with it.
export const RosterMoreActionsMenu = ({
  onImportValues,
  canImportValues,
  onSyncTechnicians,
  isSyncing = false,
  busy = false,
}: RosterMoreActionsMenuProps) => (
  <DropdownMenu>
    <DropdownMenuTrigger asChild>
      <Button
        size="icon"
        variant="secondary"
        aria-label="More roster actions"
        aria-busy={isSyncing}
        disabled={busy}
        leftIcon={
          isSyncing ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Ellipsis className="h-4 w-4" />
          )
        }
      />
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end" className="min-w-64">
      <DropdownMenuItem
        disabled={!canImportValues || busy}
        onSelect={onImportValues}
      >
        <Upload className="h-4 w-4" />
        <Stack gap="none">
          <Typography variant="bodyW3">Import values</Typography>
          {!canImportValues && (
            <Typography variant="bodyW3" textColor="tertiary">
              Available once the script sets the roster columns
            </Typography>
          )}
        </Stack>
      </DropdownMenuItem>
      {onSyncTechnicians && (
        <>
          <DropdownMenuSeparator />
          <DropdownMenuItem disabled={busy} onSelect={onSyncTechnicians}>
            {isSyncing ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            {isSyncing
              ? "Syncing technicians..."
              : "Sync ServiceTitan technicians"}
          </DropdownMenuItem>
        </>
      )}
    </DropdownMenuContent>
  </DropdownMenu>
);
